import { Classes, Icon, Intent } from "@blueprintjs/core";
import { NoAttribution, OrgRole } from "@hex/common";
import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";

import { Heading } from "../components/Heading";
import {
  CrossIcon,
  SuccessIcon,
  WarningIcon,
  WorkspaceIcon,
} from "../components/icons/CustomIcons";
import type { CliDeviceAction } from "../generated/graphqlTypes";
import {
  HexButton,
  HexCard,
  HexInputGroup,
  HexNonIdealState,
  HexSpinner,
  HexSpinnerSizes,
} from "../hex-components";
import { useAuthorizeCliDeviceMutation } from "../mutations/cliAuth.generated";
import { useMeQuery } from "../route/HexRoutes.generated";
import { Routes, useQueryParams } from "../route/routes";
import HexLogo from "../static/integration/hex-logo.inline.svg";

const Container = styled.div`
  max-width: 500px;
  margin: 40px auto;
  padding: 20px;
`;

const AuthCard = styled(HexCard)`
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  padding: 40px;

  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
  color: ${({ theme }) => theme.fontColor.DEFAULT};

  svg {
    width: 50px;
    height: 50px;
  }

  .${Classes.ICON} {
    color: ${({ theme }) => theme.fontColor.MUTED};
  }
`;

const Title = styled(Heading)`
  margin-bottom: 10px;
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const WorkspaceCard = styled(HexCard)`
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  padding: 15px 25px;
`;

const WorkspaceHeading = styled(Heading)`
  display: flex;
  align-items: center;

  .${Classes.ICON} {
    color: ${({ theme }) => theme.fontColor.MUTED};
    margin-right: 10px;
  }
`;

const UserCodeDisplay = styled.div`
  padding: 15px 25px;
  border-radius: ${({ theme }) => theme.borderRadius};
  background: ${({ theme }) => theme.backgroundColor.MUTED};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-family: monospace;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
  width: 100%;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;

  .${Classes.ICON} {
    color: ${({ theme }) => theme.intent.SUCCESS};
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  color: ${({ theme }) => theme.fontColor.DEFAULT};

  .${Classes.ICON} {
    color: ${({ theme }) => theme.intent.DANGER};
  }
`;

const NeutralContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;

  .${Classes.ICON} {
    color: ${({ theme }) => theme.fontColor.MUTED};
  }
`;

const UserCodeInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 250px;
`;

const InputLabel = styled.label`
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

const StyledInput = styled(HexInputGroup)`
  input {
    text-align: center;
    font-family: monospace;
    font-size: 18px;
    letter-spacing: 2px;
  }
`;

type AuthState = "idle" | "loading" | "success" | "error" | "denied";
type LoadingAction = "approve" | "deny" | null;

function parseErrorMessage(errMsg: string): string {
  if (
    errMsg.includes("Invalid or unknown user code") ||
    errMsg.includes("Invalid or expired user code")
  ) {
    return "Invalid code. Please check the code and try again.";
  }
  if (errMsg.includes("expired")) {
    return "This code has expired. Please restart the CLI login process.";
  }
  if (errMsg.includes("already been used")) {
    return "This code has already been used. Please restart the CLI login process.";
  }
  return errMsg;
}

export const CliAuthorizeView: React.ComponentType = React.memo(
  function CliAuthorizeView() {
    const { data, error, loading } = useMeQuery();
    const loggedOut =
      !loading && (!data?.me || error || data.me.orgRole === OrgRole.ANONYMOUS);

    const queryParams = useQueryParams();
    const userCodeFromUrl = queryParams.get("user_code") ?? "";
    const [userCodeInput, setUserCodeInput] = useState(userCodeFromUrl);
    const [authState, setAuthState] = useState<AuthState>("idle");
    const [loadingAction, setLoadingAction] = useState<LoadingAction>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Sync userCodeInput when URL changes (e.g., user navigates to different code URL)
    useEffect(() => {
      setUserCodeInput(userCodeFromUrl);
    }, [userCodeFromUrl]);

    const [authorizeCliDevice] = useAuthorizeCliDeviceMutation();

    const redirectTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    const handleUserCodeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserCodeInput(e.target.value.toUpperCase());
      },
      [],
    );

    const handleAuthorize = useCallback(
      async (action: "approve" | "deny") => {
        const code = userCodeInput.trim();
        if (!code) {
          setErrorMessage("Please enter a user code");
          return;
        }

        setAuthState("loading");
        setLoadingAction(action);
        setErrorMessage(null);

        try {
          const cliAction: CliDeviceAction =
            action === "approve" ? "APPROVE" : "DENY";
          const { data: mutationData } = await authorizeCliDevice({
            variables: {
              userCode: code,
              action: cliAction,
            },
          });

          if (mutationData?.authorizeCliDevice.success) {
            setAuthState(action === "approve" ? "success" : "denied");
          } else {
            setAuthState("error");
            setErrorMessage("An unexpected error occurred.");
          }
        } catch (err) {
          setAuthState("error");
          const errMsg =
            err instanceof Error ? err.message : "An unexpected error occurred";
          setErrorMessage(parseErrorMessage(errMsg));
        } finally {
          setLoadingAction(null);
        }
      },
      [authorizeCliDevice, userCodeInput],
    );

    const handleApprove = useCallback(
      () => handleAuthorize("approve"),
      [handleAuthorize],
    );
    const handleDeny = useCallback(
      () => handleAuthorize("deny"),
      [handleAuthorize],
    );

    if (loggedOut) {
      return (
        <Navigate
          replace={true}
          to={Routes.LOGIN.getUrl({
            attribution: NoAttribution,
            queryParams: { redirectTo },
          })}
        />
      );
    }

    if (!data && loading) {
      return <HexSpinner size={HexSpinnerSizes.LARGE} />;
    }

    if (!data || error || !data.me || !data.myOrg) {
      return (
        <HexNonIdealState
          description="There was an unexpected error. Please contact support@hex.tech for assistance."
          icon={<WarningIcon />}
          title="Something went wrong"
        />
      );
    }

    // Success state
    if (authState === "success") {
      return (
        <Container>
          <AuthCard>
            <SuccessContainer>
              <SuccessIcon iconSize={64} />
              <Title renderAs="h1">CLI Authorized</Title>
              <Description>
                You have successfully authorized the Hex CLI. You can close this
                window and return to your terminal.
              </Description>
            </SuccessContainer>
          </AuthCard>
        </Container>
      );
    }

    // Denied state
    if (authState === "denied") {
      return (
        <Container>
          <AuthCard>
            <NeutralContainer>
              <CrossIcon iconSize={64} />
              <Title renderAs="h1">Authorization Denied</Title>
              <Description>
                You have denied CLI authorization. You can close this window.
              </Description>
            </NeutralContainer>
          </AuthCard>
        </Container>
      );
    }

    return (
      <Container>
        <AuthCard>
          <LogoContainer>
            <Icon icon="console" size={50} />
            <span>+</span>
            <HexLogo />
          </LogoContainer>

          <div>
            <Title renderAs="h1">Authorize Hex CLI</Title>
            <Description>
              The Hex CLI is requesting access to your workspace.
            </Description>
          </div>

          <WorkspaceCard>
            <WorkspaceHeading renderAs="h3">
              <WorkspaceIcon />
              {data.myOrg.displayName}
            </WorkspaceHeading>
          </WorkspaceCard>

          {userCodeFromUrl ? (
            <>
              <div>
                <Description>
                  Confirm this code matches your terminal:
                </Description>
                <UserCodeDisplay>{userCodeFromUrl}</UserCodeDisplay>
              </div>
            </>
          ) : (
            <UserCodeInputContainer>
              <InputLabel htmlFor="user-code-input">
                Enter the code from your terminal
              </InputLabel>
              <StyledInput
                autoFocus={true}
                disabled={authState === "loading"}
                id="user-code-input"
                large={true}
                onChange={handleUserCodeChange}
                placeholder="XXXX-XXXX"
                value={userCodeInput}
              />
            </UserCodeInputContainer>
          )}

          {errorMessage && (
            <ErrorContainer>
              <WarningIcon iconSize={24} />
              <span>{errorMessage}</span>
            </ErrorContainer>
          )}

          <ButtonContainer>
            <HexButton
              disabled={authState === "loading"}
              large={true}
              loading={loadingAction === "deny"}
              onClick={handleDeny}
            >
              Deny
            </HexButton>
            <HexButton
              disabled={authState === "loading"}
              intent={Intent.PRIMARY}
              large={true}
              loading={loadingAction === "approve"}
              onClick={handleApprove}
            >
              Authorize
            </HexButton>
          </ButtonContainer>
        </AuthCard>
      </Container>
    );
  },
);
